/* eslint-disable class-methods-use-this */
import Cookies from 'js-cookie';
import { conditionalAssignment } from '../../lib/Object';
import { ENABLE_US_API, MULTI_REGION_COOKIE_NAME } from '../../config/env';
import { HEADERS, RequestHeaders } from './RequestHeaders';
import { ConfigBootstrapSchema } from '../../definitions/schemas';

class RequestConfigurationFactory {
  /**
   * Starts with a default value from the env variable, as the very first
   * request (to obtain the bootstrap config) will always default to a specific
   * URL. Then, the URL is updated with whatever URL was obtained from that
   * initial request, as it can vary depending on the region (data center)
   * the org is located in
   */
  private apiUrl = ENABLE_US_API;

  private equipExternalApiUrl = '';

  private isRegionReady = false;

  private region = '';

  private errorLogger = (error: Error, config?: Record<string, string>) =>
    console.error('Snag ui error', error, config);

  /**
   * Sets the API URL and the region for all subsequent requests
   */
  public setup(config: ConfigBootstrapSchema) {
    this.apiUrl = config.apiUrl;
    this.equipExternalApiUrl = config.equipExternalApiUrl;
    this.setupRegion(config.region);
  }

  public setErrorLogger = (
    errorLogger: (error: Error, config?: Record<string, string>) => void
  ) => {
    this.errorLogger = errorLogger;
  };

  /**
   * Public ErrorLogger that can be used to log errors
   * Definition of this function can be changed according to Module
   */

  public handleSnagUiError(error: Error, config?: Record<string, string>) {
    if (this.errorLogger) {
      this.errorLogger(error, config);
    }
  }

  /**
   * Sets the region value that is going to be used as a header for subsequent
   * requests. There are 3 scenarios:
   *  1. The app is used in standalone mode, in this case, the app performs a
   *     request to get the boostrap config, then passes the fetched region,
   *     which is required basically to avoid a circular dependency of the
   *     RequestHeadersFactory with Interface
   *  2. The app is used as an embedded federated module. In this case, the
   *     region is provided by the host (consumer) app. So it is set immediately
   *     as soon as the app runs, making the bootstrap request to always land
   *     on the proper region and obtain the rest of the configuration
   *  3. A region is provided with a cookie, which would be for the scenario
   *     the app is being embedded within a vendor's app, for example:
   *     in Mindtickle, as a federated module. Therefore, the region is
   *     handled by the cookie, which is forwarded in all other requests
   *     and so the region is not necessary as a header
   */
  public setupRegion(region: string) {
    if (this.isRegionProvidedByCookie() || this.isRegionReady) {
      return;
    }

    this.isRegionReady = true;
    this.region = region;
  }

  /**
   * This method can be used from anywhere in the app to get the API's URL
   * for any other request. For example: /graphql requests or directly
   * to any other server route (e.g. image thumbnails)
   */
  public getApiUrl(): string {
    return this.apiUrl;
  }

  public getEquipExternalUrl(): string {
    return this.equipExternalApiUrl;
  }

  public getGraphqlUrl(): string {
    return `${this.apiUrl}/graphql`;
  }

  public isProdUs(): boolean {
    return this.region.includes('us-');
  }

  /**
   * Combines the given values with the headers provided by this factory
   * to build the final request headers object to be used for every request.
   * Some of the given headers could be: authorization or context headers (
   * organization or contact id)
   */
  public buildRequestHeaders(baseHeaders: RequestHeaders): RequestHeaders {
    return {
      ...baseHeaders,
      ...this.buildHeaders(),
    };
  }

  private buildHeaders(): RequestHeaders {
    return {
      [HEADERS.X_REFERER]: window.location.origin,
      [HEADERS.SOURCE_PATH]: this.getProcessedPath(),
      [HEADERS.SOURCE_QUERY]: this.getProcessedQueryParams(),
      ...this.getRegionHeader(),
    };
  }

  private getProcessedQueryParams() {
    let modifiedQuery = window.location.search;
    if (modifiedQuery) {
      // Removing the leading question mark(?)
      modifiedQuery = modifiedQuery.replace(/^\?/, '');
    }
    return modifiedQuery;
  }

  private getProcessedPath() {
    let modifiedPath = window.location.pathname;
    if (modifiedPath) {
      // Converting /organization/1/insights/rooms to /organization/?/insights/rooms
      modifiedPath = modifiedPath.replace(/\/\d*\//, '/?/');
    }
    return modifiedPath;
  }

  private getRegionHeaderName(): string {
    return MULTI_REGION_COOKIE_NAME || '';
  }

  private getRegionHeader(): RequestHeaders | undefined {
    const regionHeaderName = this.getRegionHeaderName();
    if (!regionHeaderName) {
      return undefined;
    }

    return {
      ...conditionalAssignment(this.isRegionReady, {
        [regionHeaderName]: this.region,
      }),
    };
  }

  /**
   * Checks if the region is already provided by a cookie, for the scenario
   * where the app is embedded in Mindtickle. This is important as the cookie
   * is forwarded in subsequent requests, so the request to get the config
   * can be skipped and the header is then not necessary
   */
  private isRegionProvidedByCookie(): boolean {
    try {
      const regionCookieName = this.getRegionHeaderName();
      return !!(regionCookieName && Cookies.get(regionCookieName));
    } catch (e) {
      return false;
    }
  }
}

const RequestConfigurationFactoryInstance = new RequestConfigurationFactory();
export default RequestConfigurationFactoryInstance;
